/**
 * The visitor's chosen locale, as a cookie the server can read.
 *
 * ## Who decides the language, in order
 *
 *   1. **The URL**, on the public pages: `/es/…` is Spanish for everyone,
 *      including a crawler that sends no cookie and no `Accept-Language`.
 *      Those pages never read this cookie — they are static files on a CDN.
 *   2. **The account**, once signed in: `users.preferences.locale` is the
 *      durable choice, and the client copies it into this cookie on every
 *      authenticated mount (`LocaleSync`), so the next server render of a
 *      Cloud Run page is already in the right language.
 *   3. **This cookie**, signed out: set when the visitor switches language on
 *      a public page, so the sign-up funnel that follows keeps it — and the
 *      account created at the end of that funnel is seeded from it.
 *   4. `DEFAULT_LOCALE`.
 *
 * `Accept-Language` is deliberately absent: it only ever *suggests* (the
 * "Ver en español" banner), never redirects.
 *
 * Like the session hint in `lib/auth/appGate.ts`, the cookie is non-secret and
 * allowed to be wrong: a forged value changes the language a page is drawn in,
 * nothing else, and the next authenticated mount rewrites it from the account.
 */
import { DEFAULT_LOCALE, localeSchema, type Locale } from "@reflect/domain";

import { writeBrowserCookie } from "../browserCookie";
import { LIVE_LOCALES } from "./liveLocales";

/**
 * The cookie's name — next-intl's own default, so its tooling and docs apply
 * unchanged.
 */
export const LOCALE_COOKIE = "NEXT_LOCALE";

/** How long a chosen locale is remembered: one year, in seconds. */
const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Whether this build renders in more than one locale ({@link LIVE_LOCALES}).
 *
 * While it does not, nothing reads the cookie at all — reading it would make
 * every Cloud Run page dynamic for no benefit — and nothing offers a switcher.
 */
export const MULTIPLE_LOCALES = LIVE_LOCALES.length > 1;

/**
 * A live locale from an untrusted value (a cookie, a route param), or the
 * default. A catalogued locale this build has not switched on is the default
 * too: a cookie left over from staging must not surface it.
 *
 * @param value - the raw value, if any.
 * @param live - the locales switched on. Defaults to {@link LIVE_LOCALES}; a seam for tests.
 */
export function toLocale(
  value: string | undefined | null,
  live: readonly Locale[] = LIVE_LOCALES,
): Locale {
  const parsed = localeSchema.safeParse(value);
  return parsed.success && live.includes(parsed.data) ? parsed.data : DEFAULT_LOCALE;
}

/**
 * Remember `locale` in the browser (client-only).
 *
 * `SameSite=Lax` and path `/` so every page, and the sign-up funnel a public
 * page links into, sees it. `Secure` on the deployed https origins only
 * (`lib/browserCookie.ts`): local dev runs on plain http.
 *
 * @param locale - the locale to remember.
 */
export function writeLocaleCookie(locale: Locale): void {
  writeBrowserCookie(LOCALE_COOKIE, locale, LOCALE_COOKIE_MAX_AGE_SECONDS);
}

/**
 * The locale remembered in this browser, if any (client-only).
 *
 * @returns the supported locale in the cookie, or `null` when there is none
 *   (or it names a locale this build does not support).
 */
export function readLocaleCookie(): Locale | null {
  const raw = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${LOCALE_COOKIE}=`))
    ?.slice(LOCALE_COOKIE.length + 1);
  const parsed = localeSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * The locale to seed a new account with, spread into `auth.register`'s input:
 * the one this browser was last shown, or nothing.
 *
 * Nothing at all — not the default — while this build offers one locale, and
 * when the visitor never chose: "never chose" must reach the account as an
 * absent preference, so it keeps following whatever the default becomes.
 *
 * @returns `{ locale }`, or `{}`.
 */
export function signupLocaleFerry(): { locale?: Locale } {
  if (!MULTIPLE_LOCALES) return {};
  const locale = readLocaleCookie();
  return locale === null ? {} : { locale };
}
